import { useRef, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { uploadAsset, assetDownloadUrl } from "@/lib/creative";
import { Upload, Trash2, Image as ImageIcon, Film, Music } from "lucide-react";
import { toast } from "sonner";
import { LibraryPicker } from "@/components/creative/LibraryPicker";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function AssetThumb({ asset }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const u = await assetDownloadUrl(asset.id); if (alive) setUrl(u); } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [asset.id]);
  const isVideo = (asset.content_type || "").startsWith("video");
  if ((asset.content_type || "").startsWith("audio")) return <div className="studio-asset" data-testid={`asset-${asset.id}`}><div className="studio-asset-ph"><Music size={20} /></div></div>;
  return <div className="studio-asset" data-testid={`asset-${asset.id}`}>
    {url ? (isVideo ? <video src={url} muted /> : <img src={url} alt={asset.filename} />) : <div className="studio-asset-ph">{isVideo ? <Film size={20} /> : <ImageIcon size={20} />}</div>}
  </div>;
}

export default function MediaPanel({ projectId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const { data: assets } = useQuery({
    queryKey: ["studio-assets", projectId],
    queryFn: () => api.get(`/creative/assets?project_id=${projectId}`).then(r => r.data),
    enabled: !!projectId,
  });

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const kind = (file.type || "").startsWith("video") ? "video" : (file.type || "").startsWith("audio") ? "audio" : "image";
    setBusy(true);
    try {
      await uploadAsset(projectId, file, kind, setProgress);
      toast.success("Uploaded");
      qc.invalidateQueries({ queryKey: ["studio-assets", projectId] });
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const [confirm, setConfirm] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["studio-assets", projectId] }); qc.invalidateQueries({ queryKey: ["media-library"] }); };
  const remove = async (id) => {
    try { await api.delete(`/creative/assets/${id}`); refresh(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };
  const onRemove = async (a) => {
    if (a.project_id !== projectId) {
      try { await api.delete(`/creative/projects/${projectId}/library/${a.id}`); refresh(); toast.success("Removed from this project. It stays in your library."); }
      catch { toast.error("Could not remove"); }
      return;
    }
    try {
      const { data } = await api.get(`/creative/assets/${a.id}/usage`);
      const others = data.projects.filter(p => p.id !== projectId);
      if (others.length) setConfirm({ asset: a, others }); else remove(a.id);
    } catch { toast.error("Could not check where this file is used"); }
  };

  return <div data-testid="media-panel">
    <input ref={fileRef} type="file" accept="image/*,audio/*,video/mp4,video/webm,video/quicktime" onChange={onPick} style={{ display: "none" }} data-testid="asset-file-input" />
    <button className="primary-btn" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="upload-asset-button"><Upload size={16} />{busy ? "Uploading…" : "Upload photo, video or audio"}</button>
    <LibraryPicker projectId={projectId} disabled={busy} testId="media-library-picker"/>
    {progress && <p className="studio-muted" aria-live="polite" data-testid="asset-upload-progress">{progress}</p>}
    {(!assets || assets.length === 0) ? <p className="studio-muted" data-testid="assets-empty">No media yet. Upload product photos or reference videos.</p> :
      <div className="studio-asset-grid">
        {assets.map(a => <div key={a.id} className="studio-asset-wrap">
          <AssetThumb asset={a} />
          <div className="studio-asset-meta"><span title={a.filename}>{a.filename}</span><button onClick={() => onRemove(a)} data-testid={`delete-asset-${a.id}`} aria-label={a.project_id !== projectId ? "Remove from this project" : "Delete asset"} title={a.project_id !== projectId ? "Remove from this project (stays in My library)" : "Delete file"}><Trash2 size={14} /></button></div>
          {a.project_id !== projectId && <small className="studio-muted" data-testid={`asset-shared-${a.id}`}>From My library</small>}
        </div>)}
      </div>}
    <AlertDialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
      <AlertDialogContent className="sw-price-dialog" data-testid="delete-shared-dialog">
        <AlertDialogHeader><AlertDialogTitle>This file is used in other projects</AlertDialogTitle>
          <AlertDialogDescription>Deleting “{confirm?.asset.filename}” also removes it from these projects:</AlertDialogDescription></AlertDialogHeader>
        <ul style={{ margin: "4px 0 8px", color: "#dbe4ef", fontSize: 14 }} data-testid="delete-shared-projects">{confirm?.others.map(p => <li key={p.id}>• {p.name}</li>)}</ul>
        <AlertDialogFooter><AlertDialogCancel className="secondary-btn" data-testid="delete-shared-cancel">Keep it</AlertDialogCancel>
          <AlertDialogAction className="primary-btn" style={{ background: "#e5484d", color: "#fff" }} data-testid="delete-shared-confirm" onClick={() => { remove(confirm.asset.id); setConfirm(null); }}>Delete everywhere</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;
}
